import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";

// Country codes offered for phone sign-in. The mobile app builds numbers as
// "<dialCode><digits>", so these produce the same E.164 value.
const COUNTRIES = [
  { code: "+91",  label: "India (+91)" },
  { code: "+1",   label: "USA / Canada (+1)" },
  { code: "+44",  label: "United Kingdom (+44)" },
  { code: "+971", label: "UAE (+971)" },
  { code: "+61",  label: "Australia (+61)" },
  { code: "+65",  label: "Singapore (+65)" },
  { code: "+254", label: "Kenya (+254)" },
  { code: "+255", label: "Tanzania (+255)" },
  { code: "+256", label: "Uganda (+256)" },
  { code: "+968", label: "Oman (+968)" },
  { code: "+974", label: "Qatar (+974)" },
  { code: "+966", label: "Saudi Arabia (+966)" },
];

const RECAPTCHA_ID = "jb-recaptcha";

export default function AuthModal({ onClose, onSuccess }) {
  const {
    loginWithEmail, signupWithEmail, loginWithGoogle,
    sendPhoneOtp, clearPhoneVerifier,
  } = useAuth();

  const [method, setMethod] = useState("email");   // "email" | "phone"
  const [mode, setMode] = useState("login");       // email: "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  const [dialCode, setDialCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const confirmationRef = useRef(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Release the invisible reCAPTCHA when the modal goes away.
  useEffect(() => () => clearPhoneVerifier(), [clearPhoneVerifier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await loginWithEmail(email, password);
      } else {
        await signupWithEmail(email, password);
      }
      onSuccess?.();
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle();
      onSuccess?.();
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setError("");
    // Digits only; drop a leading 0 (e.g. "098765 43210" -> "9876543210").
    const digits = phone.replace(/\D/g, "").replace(/^0+/, "");
    if (digits.length < 6 || digits.length > 14) {
      setError("Please enter a valid phone number.");
      return;
    }
    setLoading(true);
    try {
      confirmationRef.current = await sendPhoneOtp(`${dialCode}${digits}`, RECAPTCHA_ID);
      setOtp("");
      setOtpSent(true);
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit code from the SMS.");
      return;
    }
    setLoading(true);
    try {
      await confirmationRef.current.confirm(otp);
      onSuccess?.();
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const switchMethod = (m) => {
    setMethod(m);
    setError("");
  };

  const changeNumber = () => {
    confirmationRef.current = null;
    setOtpSent(false);
    setOtp("");
    setError("");
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-card">
        <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>

        <h2>
          {method === "phone"
            ? "Sign in with phone"
            : mode === "login" ? "Welcome back" : "Create account"}
        </h2>
        <p className="modal-sub">
          Use the same email, Google account or phone number as in the
          Janambhoomi app, so your membership shows up there.
        </p>

        <div className="auth-tabs" role="tablist">
          <button
            type="button" role="tab" aria-selected={method === "email"}
            className={method === "email" ? "active" : ""}
            onClick={() => switchMethod("email")}
          >
            Email
          </button>
          <button
            type="button" role="tab" aria-selected={method === "phone"}
            className={method === "phone" ? "active" : ""}
            onClick={() => switchMethod("phone")}
          >
            Phone
          </button>
        </div>

        {error && <div className="error-msg">{error}</div>}

        {method === "email" && (
          <form onSubmit={handleSubmit}>
            {mode === "signup" && (
              <div className="form-group">
                <label htmlFor="auth-name">Full name</label>
                <input
                  id="auth-name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="auth-email">Email</label>
              <input
                id="auth-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="auth-password">Password</label>
              <input
                id="auth-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
              />
            </div>
            <button type="submit" className="modal-btn-full" disabled={loading}>
              {loading ? <span className="spinner" /> : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>
        )}

        {method === "phone" && !otpSent && (
          <form onSubmit={handleSendOtp}>
            <div className="form-group">
              <label htmlFor="auth-country">Country</label>
              <select
                id="auth-country"
                value={dialCode}
                onChange={(e) => setDialCode(e.target.value)}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="auth-phone">Mobile number</label>
              <div className="phone-row">
                <span className="phone-prefix">{dialCode}</span>
                <input
                  id="auth-phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  autoComplete="tel-national"
                />
              </div>
            </div>
            <button type="submit" className="modal-btn-full" disabled={loading}>
              {loading ? <span className="spinner" /> : "Send code"}
            </button>
          </form>
        )}

        {method === "phone" && otpSent && (
          <form onSubmit={handleVerifyOtp}>
            <p className="otp-hint">
              We sent a 6-digit code to <strong>{dialCode} {phone}</strong>.
            </p>
            <div className="form-group">
              <label htmlFor="auth-otp">Verification code</label>
              <input
                id="auth-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                required
                autoComplete="one-time-code"
                autoFocus
              />
            </div>
            <button type="submit" className="modal-btn-full" disabled={loading}>
              {loading ? <span className="spinner" /> : "Verify & continue"}
            </button>
            <div className="modal-switch">
              <button type="button" onClick={changeNumber}>Change number</button>
              {" · "}
              <button type="button" onClick={handleSendOtp} disabled={loading}>Resend code</button>
            </div>
          </form>
        )}

        {/* Invisible reCAPTCHA mounts here for phone sign-in. */}
        <div id={RECAPTCHA_ID} />

        <div className="modal-divider">or</div>

        <button className="google-btn" onClick={handleGoogle} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
          </svg>
          Continue with Google
        </button>

        {method === "email" && (
          <div className="modal-switch">
            {mode === "login" ? (
              <>Don't have an account?{" "}
                <button onClick={() => { setMode("signup"); setError(""); }}>Sign up</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button onClick={() => { setMode("login"); setError(""); }}>Sign in</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function friendlyError(code) {
  switch (code) {
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/popup-closed-by-user":
      return "Sign-in popup was closed. Please try again.";
    case "auth/invalid-phone-number":
      return "That phone number doesn't look right. Check the country and number.";
    case "auth/missing-phone-number":
      return "Please enter your phone number.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "auth/quota-exceeded":
      return "SMS limit reached. Please try again later.";
    case "auth/invalid-verification-code":
      return "That code is incorrect. Please check it and try again.";
    case "auth/code-expired":
    case "auth/session-expired":
      return "That code has expired. Tap Resend code to get a new one.";
    case "auth/captcha-check-failed":
      return "Verification check failed. Please refresh the page and try again.";
    case "auth/unauthorized-domain":
      return "Sign-in isn't enabled for this web address yet.";
    case "auth/operation-not-allowed":
      return "This sign-in method isn't enabled yet.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return "Something went wrong. Please try again.";
  }
}
